/**
 * Task to UseCase Mapping Utility
 * Maps stored task names (from simulation content) to UseCaseType values
 * and provides data transformation helpers for edit & rerun functionality.
 */

export type UseCaseType =
  | 'create-content'
  | 'content-testing'
  | 'get-insights'
  | 'pricing-analysis'
  | 'channel-strategy'
  | 'image-survey'
  | 'ab-test-creatives-gemini'
  | 'packaging-review-gemini'
  | 'packaging-review-v2'
  | 'packaging-variant-comparison'
  | 'attribution'
  | 'buyer-insights-report'
  | 'survey-and-focus-groups'
  | 'test-use-case'
  | 'create-campaign-strategy'
  | 'amazon-search'
  | 'logo_test'
  | 'concept-test'
  | 'driving-concept-test'
  | 'vitamin-concept-test'
  | 'claims-testing'
  | 'driving-concept-test'
  | null;

/**
 * Maps stored task names (from database) to UseCaseType values
 * Some tasks have different names when stored vs. used as UseCaseType
 */
export const taskToUseCaseMap: Record<string, UseCaseType> = {
  // Packaging
  'packaging-review-v2': 'packaging-review-v2',
  'packaging-review-gemini': 'packaging-review-gemini',
  'packaging-variant-comparison': 'packaging-variant-comparison',
  
  // Survey
  'survey_and_focus_groups': 'survey-and-focus-groups',
  
  // Logo (handle both underscore and dash variations)
  'logo_test': 'logo_test',
  'logo-test': 'logo_test',
  
  // Creative tests
  'channel-strategy': 'channel-strategy',
  'channel-event-strategy': 'channel-strategy',
  'creative-review': 'channel-strategy',
  'image-survey': 'image-survey',
  'ab-test-creatives-gemini': 'ab-test-creatives-gemini',
  
  // Content creation
  'create-content': 'create-content',
  'create-content-short': 'create-content',
  'create-content-long': 'create-content',
  
  // Content testing
  'content-testing': 'content-testing',
  'a/b-multivariate-tests': 'content-testing',
  
  // Insights & Analysis
  'get-insights': 'get-insights',
  'pricing-analysis': 'pricing-analysis',
  
  // Attribution / Messaging
  'attribution': 'attribution',
  'ab-test-messaging': 'attribution',
  
  // Buyer insights
  'buyer-insights-report': 'buyer-insights-report',
  'buyer-insights-report-b2b': 'buyer-insights-report',
  'buyer-insights-report-b2c': 'buyer-insights-report',
  
  // Campaign
  'create_campaign_strategy': 'create-campaign-strategy',
  
  // Amazon
  'amazon-search': 'amazon-search',
  
  // Test
  'test_use_case': 'test-use-case',
  
  // Concept Test
  'concept-test': 'concept-test',

  // Driving Concept Test
  'driving-concept-test': 'driving-concept-test',

  // Vitamin Concept Test
  'vitamin-concept-test': 'vitamin-concept-test',

  // Claims Testing
  'claims_testing': 'claims-testing',
};

/**
 * Get UseCaseType from stored task name
 */
export function getUseCaseFromTask(task: string | undefined): UseCaseType {
  if (!task) return null;
  return taskToUseCaseMap[task] || null;
}

/**
 * Content data interface (from simulation.content JSON field)
 */
export interface ContentData {
  [key: string]: unknown;
  audience_name?: string;
  name?: string;
  goal?: string;
  task?: string;
  content_type?: string;
  segment_ids?: number[];
  persona_filters?: Record<string, Record<string, unknown>>;
  current_logo?: string;
  images?: string[];
  questions?: any[];
  context?: string;
  // Packaging specific (nested in data object)
  data?: {
    brand?: string;
    product_context?: string;
    placement_type?: string;
    design_guideline?: string;
    packaging_images?: string[];
    competitor_images?: string[];
    [key: string]: unknown;
  };
  // Content creation specific
  content_subject?: string;
  company_context?: string;
  is_deep_simulation?: boolean;
  // Attribution specific
  messages?: string[];
  message_to_test?: string;
  // Buyer insights specific
  business_model?: string;
  // Amazon search specific
  product_name?: string;
  search_terms?: string[];
  // Dynamic segmentation
  dynamic_segmentation_id?: number | null;
  dynamic_segment_indices?: number[];
}

/**
 * Convert parsed questions array back to human-readable text format
 * Format:
 * Q1: Question text (question_type)
 * - Option 1
 * - Option 2
 * 
 * Q2: Next question (type)
 * ...
 */
export function convertParsedQuestionsToText(questions: any[]): string {
  if (!questions || !Array.isArray(questions) || questions.length === 0) {
    return '';
  }

  const lines: string[] = [];

  questions.forEach((question, index) => {
    // Question header: Q1: Question text (type)
    const questionId = question.id || `Q${index + 1}`;
    const questionText = question.text || '';
    const questionType = question.type ? `(${question.type.replace(/_/g, ' ')})` : '';
    
    lines.push(`${questionId}: ${questionText} ${questionType}`.trim());

    // Add choices/options if they exist
    if (question.choices && Array.isArray(question.choices) && question.choices.length > 0) {
      question.choices.forEach((choice: string) => {
        lines.push(`- ${choice}`);
      });
    }

    // Add scale info if it's a rating scale (only if values are actual numbers)
    if (question.scale_min != null && question.scale_max != null && 
        typeof question.scale_min === 'number' && typeof question.scale_max === 'number') {
      lines.push(`Scale: ${question.scale_min} to ${question.scale_max}`);
    }

    // Add maxdiff items if present
    if (question.maxdiff_items && Array.isArray(question.maxdiff_items) && question.maxdiff_items.length > 0) {
      lines.push('MaxDiff Items:');
      question.maxdiff_items.forEach((item: string) => {
        lines.push(`- ${item}`);
      });
    }

    // Add conjoint attributes if present
    if (question.conjoint_attributes && Array.isArray(question.conjoint_attributes) && question.conjoint_attributes.length > 0) {
      lines.push('Conjoint Attributes:');
      question.conjoint_attributes.forEach((attr: any) => {
        const levels = attr.levels?.join(', ') || '';
        lines.push(`- ${attr.name}: ${levels}`);
      });
    }

    // Add blank line between questions (except after the last one)
    if (index < questions.length - 1) {
      lines.push('');
    }
  });

  return lines.join('\n');
}

/**
 * Transform contentData to initialFormData format for each use case type
 * Returns the data structure expected by each form's initialFormData prop
 */
export function transformContentDataToFormData(
  contentData: ContentData,
  useCaseType: UseCaseType
): Record<string, any> {
  const baseFormData: Record<string, any> = {
    simName: contentData.name || '',
    goal: contentData.goal || '',
  };

  switch (useCaseType) {
    case 'packaging-review-v2': {
      const packagingData = contentData.data || {};
      return {
        ...baseFormData,
        brand: packagingData.brand || '',
        productContext: packagingData.product_context || '',
        placementType: packagingData.placement_type || '',
        designGuideline: packagingData.design_guideline || '',
        analysisType: packagingData.analysis_type || 'all',
        frontImage: packagingData.front_image || null,
        sideImages: packagingData.side_images || [],
        backImages: packagingData.back_images || [],
        competitorImages: packagingData.competitor_images || [],
      };
    }

    case 'packaging-variant-comparison': {
      const variantData = contentData.data || {};
      return {
        ...baseFormData,
        brand: variantData.brand || '',
        goal: variantData.goal || '',
        variantImages: variantData.variant_images || [],
      };
    }

    case 'survey-and-focus-groups': {
      const questions = contentData.questions || [];
      const hasQuestions = Array.isArray(questions) && questions.length > 0;
      
      return {
        ...baseFormData,
        context: contentData.context || '',
        // Convert parsed questions back to readable text for the textarea
        questionsText: hasQuestions ? convertParsedQuestionsToText(questions) : '',
        // Pass parsed questions for initializing images
        parsedQuestions: questions,
        // Start from initial step - user will need to re-parse
        useParsedQuestions: false,
      };
    }

    case 'logo_test': {
      // Check both top-level and nested data for images (some may be stored in data object)
      const nestedData = contentData.data || {};
      return {
        ...baseFormData,
        // Images might be at top level or nested in data
        images: contentData.images || (nestedData as any).images || [],
        currentLogo: contentData.current_logo || (nestedData as any).current_logo || null,
      };
    }

    case 'channel-strategy': {
      const data = contentData.data || {};
      return {
        ...baseFormData,
        reviewType: contentData.task === 'creative-review' ? 'creative-review' : 'ab-test',
        // Creative Review specific fields
        campaignObjective: (contentData as any).campaign_objective || data.campaign_objective || '',
        kpis: (contentData as any).kpis || data.kpis || '',
        companyDetails: (contentData as any).company_details || data.company_details || '',
        productDetails: (contentData as any).product_details || data.product_details || '',
        competitiveLandscape: (contentData as any).competitive_landscape || data.competitive_landscape || '',
        mediumAndPlacement: (contentData as any).medium_and_placement || data.medium_and_placement || '',
        culturalConsiderations: (contentData as any).cultural_considerations || data.cultural_considerations || '',
        priorPerformanceData: (contentData as any).prior_performance_data || data.prior_performance_data || '',
        // Images
        images: contentData.images || data.images || [],
      };
    }

    case 'image-survey': {
      return {
        ...baseFormData,
        creative_type: (contentData as any).creative_type || '',
        industry: (contentData as any).industry || '',
        placement_goal: (contentData as any).placement_goal || '',
        // Images
        images: contentData.images || [],
      };
    }

    case 'ab-test-creatives-gemini': {
      const data = contentData.data || {};
      return {
        ...baseFormData,
        context: contentData.context || data.context || '',
        // Images
        images: contentData.images || data.images || [],
      };
    }

    case 'packaging-review-gemini': {
      // Images are stored at top level for packaging-review-gemini
      const packagingData = contentData.data || {};
      return {
        ...baseFormData,
        brand: packagingData.brand || '',
        productContext: packagingData.product_context || '',
        // Images - check top level first (where form submits), then nested
        images: contentData.images || packagingData.packaging_images || [],
      };
    }

    case 'create-content': {
      const isLongForm = contentData.task === 'create-content-long';
      return {
        ...baseFormData,
        contentType: contentData.content_type || '',
        contentSubject: contentData.content_subject || '',
        companyContext: contentData.company_context || '',
        contentFormType: isLongForm ? 'long' : 'short',
        simulationMode: contentData.is_deep_simulation ? 'advanced' : 'basic',
      };
    }

    case 'content-testing': {
      return {
        ...baseFormData,
        contentType: contentData.content_type || '',
        contentSubject: contentData.content_subject || '',
        companyContext: contentData.company_context || '',
        objective: (contentData as any).objective || contentData.goal || '',
      };
    }

    case 'get-insights': {
      // InsightsForm uses questions array and context
      const insightQuestions = (contentData as any).questions || [];
      return {
        ...baseFormData,
        context: contentData.context || '',
        questions: Array.isArray(insightQuestions) ? insightQuestions : [''],
      };
    }

    case 'pricing-analysis': {
      return {
        ...baseFormData,
        context: contentData.context || '',
      };
    }

    case 'attribution': {
      // AttributionForm uses objective, placement, marketingCopies
      const copies = (contentData as any).marketing_copies || (contentData as any).messages || [];
      return {
        ...baseFormData,
        objective: (contentData as any).objective || contentData.goal || '',
        placement: (contentData as any).placement || '',
        marketingCopies: Array.isArray(copies) && copies.length > 0 ? copies : [''],
      };
    }

    case 'buyer-insights-report': {
      const isB2B = contentData.task?.includes('b2b');
      // Additional data contains website_url and product_name
      const additionalData = (contentData as any).additional_data || (contentData as any).data || {};
      return {
        ...baseFormData,
        context: contentData.context || '',
        businessModel: isB2B ? 'B2B' : 'B2C',
        // Map stored field names to form field names
        websiteUrl: additionalData.website_url || (contentData as any).website_url || '',
        productName: additionalData.product_name || (contentData as any).product_name || '',
      };
    }

    case 'create-campaign-strategy': {
      return {
        ...baseFormData,
        context: contentData.context || '',
        objective: (contentData as any).objective || contentData.goal || '',
      };
    }

    case 'amazon-search': {
      return {
        ...baseFormData,
        productName: contentData.product_name || '',
        searchTerms: contentData.search_terms || [],
      };
    }

    case 'test-use-case': {
      return {
        ...baseFormData,
        context: contentData.context || '',
      };
    }

    case 'concept-test': {
      // Handle hero_image which can be stored as object with image and description
      const heroImage = (contentData as any).hero_image || {};
      const heroImageData = typeof heroImage === 'string' ? heroImage : heroImage.image;
      const heroImageDesc = typeof heroImage === 'object' ? heroImage.description : '';
      
      return {
        ...baseFormData,
        headline: (contentData as any).headline || '',
        consumerInsight: (contentData as any).consumer_insight || '',
        mainBenefit: (contentData as any).main_benefit || '',
        reasonsTobelieve: (contentData as any).reasons_to_believe || [''],
        pricePoint: (contentData as any).price_point || '',
        packSize: (contentData as any).pack_size || '',
        brand: (contentData as any).brand || '',
        heroImageBase64: heroImageData || null,
        heroImagePreview: heroImageData || null,
        heroImageDescription: heroImageDesc || '',
        alternativeProducts: (contentData as any).alternative_products || [''],
      };
    }

    case 'driving-concept-test': {
      return {
        ...baseFormData,
        conceptPreset: '',
        conceptStatement: (contentData as any).concept_statement || '',
        sampleSize: (contentData as any).sample_size || 10,
        weather: (contentData as any).weather || 'clear',
        traffic: (contentData as any).traffic || 'moderate',
        population: (contentData as any).population || 'population_driving_consumer',
        llmModel: (contentData as any).llm_model || 'o3',
      };
    }

    case 'vitamin-concept-test': {
      // Restore the segment / sub-segment selection from the stored filter so a
      // rerun reopens the form with the same audience scope.
      const cd = contentData as Record<string, unknown>;
      const segmentRestore = (() => {
        const sf = cd.segment_filter;
        if (Array.isArray(sf) && sf.length > 0) {
          const subSelections: Record<string, string[]> = {};
          sf.forEach((raw) => {
            const entry = raw as { segment?: string; sub_segments?: string[] };
            if (entry?.segment && Array.isArray(entry.sub_segments) && entry.sub_segments.length > 0) {
              subSelections[entry.segment] = entry.sub_segments;
            }
          });
          return { allAgents: false, subSelections, selectedSegments: [] as string[] };
        }
        const segs = cd.segments;
        if (Array.isArray(segs) && segs.length > 0) {
          return { allAgents: false, subSelections: {}, selectedSegments: segs as string[] };
        }
        return { allAgents: true, subSelections: {}, selectedSegments: [] as string[] };
      })();

      // Check for direct concept_str (single concept mode) - this is the actual stored format
      if ((contentData as any).concept_str) {
        return {
          ...baseFormData,
          ...segmentRestore,
          conceptStr: (contentData as any).concept_str,
          sampleSize: (contentData as any).sample_size ?? 0,
          population: (contentData as any).population || 'population_vitamin_survey_enriched_copy',
        };
      }

      // Check for concepts array (multi-concept mode)
      if ((contentData as any).concepts && Array.isArray((contentData as any).concepts)) {
        return {
          ...baseFormData,
          ...segmentRestore,
          concepts: (contentData as any).concepts.map((c: any) => ({
            conceptStr: c.concept_str || '',
          })),
          sampleSize: (contentData as any).sample_size ?? 0,
          population: (contentData as any).population || 'population_vitamin_survey_enriched_copy',
        };
      }

      // Fallback: try to build from structured fields (legacy format)
      const parts: string[] = [];
      if ((contentData as any).product_headline) parts.push(`**Product Headline:** ${(contentData as any).product_headline}`);
      if ((contentData as any).brand_name) parts.push(`**Brand Name:** ${(contentData as any).brand_name}`);
      if ((contentData as any).consumer_insight) parts.push(`**One Line Description:** ${(contentData as any).consumer_insight}`);
      if ((contentData as any).main_benefit) parts.push(`**Detailed Description:** ${(contentData as any).main_benefit}`);

      const rtbs = (contentData as any).reasons_to_believe || [];
      if (Array.isArray(rtbs) && rtbs.length > 0) {
        parts.push(`**Reasons to Believe:**\n${rtbs.map((r: string) => `- ${r}`).join('\n')}`);
      }

      const pricePack = [(contentData as any).price_point, (contentData as any).pack_size].filter(Boolean).join(' for ');
      if (pricePack) parts.push(`**Price & Pack:** ${pricePack}`);
      if ((contentData as any).product_image_url) parts.push(`**Hero Image URL:** ${(contentData as any).product_image_url}`);

      const comps = (contentData as any).competitor_products || [];
      if (Array.isArray(comps) && comps.length > 0) {
        parts.push(`**Alternative Products:** ${comps.join(', ')}`);
      }

      return {
        ...baseFormData,
        conceptStr: parts.join('\n\n'),
        productHeadline: (contentData as any).product_headline || '',
        brandName: (contentData as any).brand_name || '',
        consumerInsight: (contentData as any).consumer_insight || '',
        mainBenefit: (contentData as any).main_benefit || '',
        reasonsToBelieve: (contentData as any).reasons_to_believe || [],
        pricePoint: (contentData as any).price_point || '',
        packSize: (contentData as any).pack_size || '',
        productImageUrl: (contentData as any).product_image_url || '',
        competitorProducts: (contentData as any).competitor_products || [],
        sampleSize: (contentData as any).sample_size ?? 0,
        population: (contentData as any).population || 'population_vitamin_survey_enriched_copy',
      };
    }

    case 'claims-testing': {
      const claimsData = contentData.data || {};
      const claims = (claimsData as any).claims || [];
      return {
        ...baseFormData,
        productName: (claimsData as any).product_name || '',
        productDescription: (claimsData as any).product_description || '',
        category: (claimsData as any).category || '',
        claimsText: claims.join('\n'),
        context: (claimsData as any).context || '',
      };
    }

    default:
      return baseFormData;
  }
}

/**
 * Get display name for a use case type
 */
export function getUseCaseDisplayName(useCaseType: UseCaseType): string {
  const displayNames: Record<string, string> = {
    'packaging-review-v2': 'Packaging Review',
    'packaging-review-gemini': 'Packaging Review (Gemini)',
    'packaging-variant-comparison': 'Packaging Variant Comparison',
    'survey-and-focus-groups': 'Survey & Focus Groups',
    'logo_test': 'Logo Test',
    'channel-strategy': 'A/B Test Creatives & Ads',
    'image-survey': 'Creative Image Click/Rank Test',
    'ab-test-creatives-gemini': 'A/B Test Creatives (Gemini)',
    'create-content': 'Create Content',
    'content-testing': 'Test Content',
    'get-insights': 'Get Insights',
    'pricing-analysis': 'Pricing Analysis',
    'attribution': 'A/B Test Messaging',
    'buyer-insights-report': 'Buyer Insights Report',
    'create-campaign-strategy': 'Create Campaign Strategy',
    'amazon-search': 'Amazon Search',
    'test-use-case': 'Test Use Case',
    'concept-test': 'Concept Test',
    'driving-concept-test': 'Concept Test',
    'vitamin-concept-test': 'Concept Test',
    'claims-testing': 'Claims Testing',
  };

  return displayNames[useCaseType || ''] || 'Unknown';
}
